/** Paddle.js, loaded from Paddle's CDN once, or the stub a test injected as window.Paddle. */
import type { AppConfig } from "../config";

export type CheckoutInput = {
  priceId: string;
  customerId: string;
  workspaceId: string;
  promo?: string | null;
  successUrl: string;
};

type PaddleJs = {
  Environment: { set(env: "sandbox" | "production"): void };
  Initialize(opts: { token: string }): void;
  Checkout: { open(opts: Record<string, unknown>): void };
};

declare global {
  interface Window {
    Paddle?: PaddleJs;
  }
}

let loading: Promise<PaddleJs> | null = null;

function load(cfg: AppConfig): Promise<PaddleJs> {
  if (window.Paddle) return Promise.resolve(window.Paddle);
  if (!cfg.paddle) return Promise.reject(new Error("paddle_not_configured"));
  const { token, environment } = cfg.paddle;
  loading ??= new Promise<PaddleJs>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://cdn.paddle.com/paddle/v2/paddle.js";
    s.onload = () => {
      const p = window.Paddle;
      if (!p) return reject(new Error("paddle_failed_to_load"));
      if (environment === "sandbox") p.Environment.set("sandbox");
      p.Initialize({ token });
      resolve(p);
    };
    s.onerror = () => reject(new Error("paddle_failed_to_load"));
    document.head.appendChild(s);
  });
  return loading;
}

/** Paddle sends a customer to the default payment link with `?_ptxn=` to pay a transaction
 * (a card update, a retried renewal). Paddle.js would open it itself on load, but it loads
 * here only when a plan is chosen, and the router can rewrite the address before it
 * arrives — so the id is read at start-up, dropped from the address so Paddle.js does not
 * open it twice, and the transaction opened by name. */
export function openPaymentLink(cfg: AppConfig, search: string): boolean {
  const txn = new URLSearchParams(search).get("_ptxn");
  if (!txn || !(cfg.paddle || window.Paddle)) return false;
  const url = new URL(window.location.href);
  url.searchParams.delete("_ptxn");
  window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
  void load(cfg)
    .then((paddle) =>
      paddle.Checkout.open({
        transactionId: txn,
        settings: { displayMode: "overlay", locale: "en" },
      }),
    )
    // Paddle's CDN unreachable: the page still works, and the customer can retry the link.
    .catch(() => undefined);
  return true;
}

/** The overlay flavour (spec §6.1 step 3): in-page, so the SPA can run on localhost. The
 * 7-day trial with card capture is configured on the price in Paddle, not per call. */
export async function openCheckout(cfg: AppConfig, input: CheckoutInput): Promise<void> {
  const paddle = await load(cfg);
  paddle.Checkout.open({
    items: [{ priceId: input.priceId, quantity: 1 }],
    customer: { id: input.customerId },
    customData: { workspace_id: input.workspaceId },
    ...(input.promo ? { discountCode: input.promo } : {}),
    settings: { displayMode: "overlay", successUrl: input.successUrl, locale: "en" },
  });
}
